/**
 * Leave credits — the kinds of leave the organization grants, and what one employee has.
 *
 * Two surfaces read these: the Leave Types catalog under Personnel → Settings, and the Leave
 * Credits panel on an employee's profile. They share this file because they share the wire.
 */

/*
  NUMERIC ON THE WIRE. No `JsonStringEnumConverter` is configured, so an enum crosses as
  0..3 — a string union would typecheck perfectly and make every comparison silently false.
  `String(value)` into a <Select>, `Number(next)` back out.
*/

/** How a leave type is tracked. */
export const LeaveCreditKind = {
  /** Carries a running balance built from ledger rows — Vacation, Sick, Compensatory. */
  Ledger: 0,
  /** Granted as a whole entitlement, with no running balance — Maternity, Study, SPL. */
  Grant: 1,
} as const;

export type LeaveCreditKindValue = (typeof LeaveCreditKind)[keyof typeof LeaveCreditKind];

/**
 * What one ledger row is.
 *
 * The members are the Credit Balance table's own column headings, deliberately: a reader
 * asking why Earned says 3.75 should find rows called Earned.
 */
export const LeaveLedgerEntryType = {
  BeginningBalance: 0,
  Earned: 1,
  Used: 2,
  Adjustment: 3,
} as const;

export type LeaveLedgerEntryTypeValue =
  (typeof LeaveLedgerEntryType)[keyof typeof LeaveLedgerEntryType];

/** Where a ledger row stands with its approver. Only Approved rows count toward a balance. */
export const LeaveEntryStatus = {
  Pending: 0,
  Approved: 1,
  Disapproved: 2,
  Cancelled: 3,
} as const;

export type LeaveEntryStatusValue = (typeof LeaveEntryStatus)[keyof typeof LeaveEntryStatus];

/** One kind of leave the organization grants. */
export interface LeaveType {
  id: string;
  name: string;
  /** The short form — "VL". Optional; a type may genuinely have none. */
  code: string | null;
  description: string | null;
  creditKind: LeaveCreditKindValue;
  /** Days earned per month. Null where the type does not accrue by the calendar. */
  monthlyAccrualDays: number | null;
  sortOrder: number;

  /** The type this one sits under; null on a top-level type. One level deep. */
  parentLeaveTypeId: string | null;
  parentLeaveTypeName: string | null;
  /** A child drawing on its parent's credit rather than keeping its own count. */
  sharesParentCredit: boolean;
  /** Live child types. A type with any cannot become a child, and cannot be deleted. */
  childCount: number;
  /** Live children drawing on this type's credit. While any do, it cannot become granted. */
  sharingChildCount: number;
  /** Whether the parent is live — a child is not restored under a deleted parent. */
  parentIsActive: boolean;

  /** How many ledger entries name this type. Live rows only. */
  recordCount: number;

  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

/** The position in the list is the server's to assign, so it is not here. */
export interface LeaveTypeRequest {
  name: string;
  code: string | null;
  description: string | null;
  creditKind: LeaveCreditKindValue;
  monthlyAccrualDays: number | null;
  parentLeaveTypeId: string | null;
  /** Required when a parent is named — own count (false) or the parent's credit (true). */
  sharesParentCredit: boolean | null;
}

/**
 * One row of the Credit Balance table.
 *
 * `Balance = Beginning + Earned - Used + Adjusted`, over approved rows only. Every figure is
 * derived from the ledger on read — there is no stored counter anywhere.
 */
export interface LeaveCreditBalance {
  leaveTypeId: string;
  leaveTypeName: string;
  leaveTypeCode: string | null;
  creditKind: LeaveCreditKindValue;
  sortOrder: number;

  /** The parent row this one is drawn under, indented. Rows arrive parent-then-children. */
  parentLeaveTypeId: string | null;

  /**
   * The credit is the parent's: the row states only Used (and Awaiting approval), and the
   * PARENT's Used and Balance include it. Beginning, Earned, Adjusted and Balance are null here.
   */
  sharesParentCredit: boolean;

  beginning: number | null;
  earned: number | null;

  /**
   * Consumed, positive. **Null for a granted type**, which has no ledger to draw down — the
   * cell renders an em dash, which says the column does not apply rather than reporting none.
   */
  used: number | null;

  /** Corrections, signed. Null for a granted type, for the reason `used` is. */
  adjusted: number | null;

  /** Null only on a sharing child, whose balance is the parent's. */
  balance: number | null;

  /**
   * Days filed but not yet decided, or **null when nothing is in flight** — an em dash rather
   * than `0.00`. With leave requests unbuilt this is null on every row today.
   */
  awaitingApproval: number | null;

  /**
   * What the rows prove may be wrong with this balance — the row tints red and says why on
   * hover. Empty when nothing is; always empty on a sharing child (its parent answers for it).
   */
  discrepancies: LeaveBalanceDiscrepancyValue[];
}

/** NUMERIC on the wire, mirroring the server's `LeaveBalanceDiscrepancy` member for member. */
export const LeaveBalanceDiscrepancy = {
  NegativeBalance: 0,
  WentBelowZero: 1,
  // 2 was NoBeginningBalance, withdrawn 27 Oct 2026 — not reused.
} as const;

export type LeaveBalanceDiscrepancyValue =
  (typeof LeaveBalanceDiscrepancy)[keyof typeof LeaveBalanceDiscrepancy];

/**
 * The tooltip's sentence for each — a POSSIBLE discrepancy, stating what the rows show and
 * never concluding what went wrong.
 */
export const LEAVE_BALANCE_DISCREPANCY_TEXT: Record<LeaveBalanceDiscrepancyValue, string> = {
  [LeaveBalanceDiscrepancy.NegativeBalance]: 'The balance is below zero.',
  [LeaveBalanceDiscrepancy.WentBelowZero]:
    'Read by effective date, the balance fell below zero at some point — leave was used before the credit behind it was recorded.',
};

/** One line of the Leave Ledger. */
export interface LeaveLedgerEntry {
  id: string;
  personnelId: string;
  leaveTypeId: string;
  leaveTypeName: string;
  entryType: LeaveLedgerEntryTypeValue;
  /** Signed, as stored: negative for credit used. */
  days: number;
  /** A calendar date — `YYYY-MM-DD`, never an instant. */
  effectiveDate: string;
  reason: string | null;
  status: LeaveEntryStatusValue;
  /**
   * The Leave History record that wrote this entry — its deduction. Such a row is changed
   * through the record; the ledger refuses to edit, delete or restore it.
   */
  leaveRecordId?: string | null;
  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

/**
 * Records one credit-affecting event.
 *
 * `days` is sent POSITIVE for everything but an adjustment; the server applies the sign from
 * `entryType`. An adjustment carries its own sign, because its direction is what the operator
 * is choosing.
 */
export interface LeaveLedgerEntryRequest {
  leaveTypeId: string;
  entryType: LeaveLedgerEntryTypeValue;
  days: number;
  effectiveDate: string;
  reason: string | null;
}

/**
 * One line of the Leave History — a leave taken, every one of them approved.
 *
 * Nullable fields are `?:` as well as `| null`: a null crosses the wire OMITTED, so compare
 * them with `== null`.
 */
export interface LeaveRecord {
  id: string;
  personnelId: string;
  leaveTypeId: string;
  leaveTypeName: string;
  /** Calendar dates — `YYYY-MM-DD`, never instants. */
  startDate: string;
  endDate: string;
  days: number;
  /** Whether the days came off the type's credit, or the leave is recorded only. */
  deductsCredit: boolean;
  /** The type that covered what the leave type lacked — absent when none was needed. */
  supplementaryLeaveTypeId?: string | null;
  supplementaryLeaveTypeName?: string | null;
  /** Days charged to the supplementary type; the leave type was charged `days` minus these. */
  supplementaryDays?: number | null;
  filedByUserId?: string | null;
  filedByName?: string | null;
  filedDate?: string | null;
  reason?: string | null;
  approvedByUserId?: string | null;
  approvedByName?: string | null;
  approvedDate: string;
  approvalRemarks?: string | null;
  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

/** Records or corrects one approved leave. `deductsCredit` is required: null is refused. */
export interface LeaveRecordRequest {
  leaveTypeId: string;
  startDate: string;
  endDate: string;
  days: number;
  deductsCredit: boolean | null;
  /** Charged whatever the leave type cannot cover. The split is the server's, from live balances. */
  supplementaryLeaveTypeId: string | null;
  filedByUserId: string | null;
  filedDate: string | null;
  reason: string | null;
  approvedByUserId: string | null;
  approvedDate: string | null;
  approvalRemarks: string | null;
}

/** What each entry type is called on screen. The backend has no label for these. */
export const LEAVE_ENTRY_TYPE_LABELS: Record<LeaveLedgerEntryTypeValue, string> = {
  [LeaveLedgerEntryType.BeginningBalance]: 'Beginning balance',
  [LeaveLedgerEntryType.Earned]: 'Earned',
  [LeaveLedgerEntryType.Used]: 'Used',
  [LeaveLedgerEntryType.Adjustment]: 'Adjustment',
};

/** What each credit kind is called on screen. */
export const LEAVE_CREDIT_KIND_LABELS: Record<LeaveCreditKindValue, string> = {
  [LeaveCreditKind.Ledger]: 'Credit ledger',
  [LeaveCreditKind.Grant]: 'Granted',
};

/** What each status is called on screen. */
export const LEAVE_ENTRY_STATUS_LABELS: Record<LeaveEntryStatusValue, string> = {
  [LeaveEntryStatus.Pending]: 'Awaiting approval',
  [LeaveEntryStatus.Approved]: 'Approved',
  [LeaveEntryStatus.Disapproved]: 'Disapproved',
  [LeaveEntryStatus.Cancelled]: 'Cancelled',
};

/**
 * A child type's answer to "own count, or the parent's?" — the words the form asks it in.
 * Null on a top-level type, which has no parent to share with: the cell reads an em dash.
 */
export function leaveCreditShareLabel(type: Pick<LeaveType, 'parentLeaveTypeId' | 'sharesParentCredit'>): string | null {
  if (type.parentLeaveTypeId == null) return null;
  return type.sharesParentCredit ? "Shares parent's credit" : 'Own count';
}
